import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ClassicFormal, ArtisticCollage, ModernMinimal, TropicalBeach, NoirVinyl, LightVinyl, PremiumStory } from '../components/RSVPTemplates';
// Phase 1.9.5 — Envelope animation removed. The new cinematic Opener
// (Phase 1.9) replaces the envelope as the entry experience.
import TemplateRenderer from '../components/renderer/TemplateRenderer';
import { V2_TEMPLATES } from '../lib/utils/v2Templates';
import { Loader2 } from 'lucide-react';
import PublicLanguageSwitcher from '../components/public/PublicLanguageSwitcher';
import {
  detectGuestLanguage,
  saveGuestLanguagePreference,
  resolveInvitationContent,
  resolveV1Field,
  resolveSettingsOverrides,
} from '../lib/i18nInvite';
import PerfAuditOverlay from '../components/PerfAuditOverlay';
import LayoutDebugOverlay from '../components/public/LayoutDebugOverlay';
import PreviewDraftBadge from '../components/public/PreviewDraftBadge';
import NewVersionBanner from '../components/editor/NewVersionBanner';
import { purgeStaleServiceWorkers } from '../lib/utils/purgeStaleServiceWorkers';
import useApplyImageDimensions from '../lib/utils/useApplyImageDimensions';
import OpenerOverlay, { shouldPlayOpener } from '../components/openers/OpenerOverlay';
import { InvitationAudioProvider, resolveInvitationMusicUrl, useInvitationAudio } from '../components/audio/InvitationAudioContext';
import EntryCover, { ENTRY_COVER_DEFAULTS } from '../components/entry/EntryCover';

const API = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_COMPONENTS = {
  classic_formal: ClassicFormal,
  artistic_collage: ArtisticCollage,
  modern_minimal: ModernMinimal,
  tropical_beach: TropicalBeach,
  noir_vinyl: NoirVinyl,
  light_vinyl: LightVinyl,
  premium_story: PremiumStory,
};

export default function PublicInvite() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('preview');
  const isShowcase = searchParams.get('showcase') === 'true';
  // Dev-only perf overlay. Strictly opt-in via URL param. Never shown to
  // normal guests, never sends data, read-only DOM scans throttled to 1s.
  const perfAuditEnabled = searchParams.get('perfaudit') === '1';
  // Layout debug — surfaces viewport / isMobile / each element's
  // bounding rect so designers can compare desktop incognito vs.
  // phone incognito with one URL. Opt-in only.
  const layoutDebugEnabled = searchParams.get('debug') === 'layout';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rsvpPage, setRsvpPage] = useState(null);
  const [event, setEvent] = useState(null);
  // True when the response represents the unpublished draft (preview
  // token matched). Drives the small floating "Preview · Draft" badge
  // so designers (and only designers — guests never set ?preview=)
  // know which version their device is rendering. Critical for
  // debugging "old image on phone" reports.
  const [isPreviewResponse, setIsPreviewResponse] = useState(false);
  // Phase 1.9.8 — gentle crossfade between opener and invitation.
  // `revealTriggered` is set by OpenerOverlay ~120ms after exit fade
  // starts. The actual phase ('idle' | 'active' | 'revealing' | 'done')
  // is derived synchronously below so the first paint already has the
  // correct invitation state (avoids a one-frame flicker on load).
  const [revealTriggered, setRevealTriggered] = useState(false);
  const handleOpenerExitStart = useCallback(() => {
    // The user's spec asks the invitation reveal to begin ~100–200ms
    // AFTER the opener fade starts. Defer ~120ms.
    setTimeout(() => setRevealTriggered(true), 120);
  }, []);

  // Phase 1.9.2 — once the opener has finished (natural / Skip / error)
  // we MUST stop rendering it so React fully unmounts the component and
  // its useEffect cleanup runs (which releases the scroll lock). Without
  // this flag the overlay returned null internally but stayed mounted
  // → scroll lock leaked. See OpenerOverlay.js Phase 1.9.2 header.
  const [openerCompleted, setOpenerCompleted] = useState(false);
  // Phase 1.9.3 — guest-initiated Replay. Manual replays bypass both the
  // seen-state localStorage and the openerCompleted flag, while preserving
  // invitation/RSVP/music/scroll. `replayKey` forces a fresh mount each
  // time so OpenerOverlay's useEffect runs clean (no timer/DOM leaks).
  // `replayScrollY` is captured BEFORE remount and restored on complete.
  const [replayKey, setReplayKey] = useState(0);
  const replayScrollY = useRef(0);
  const isReplaying = replayKey > 0 && !openerCompleted;

  // Phase 1.9.19 — Entry Cover state. Lives at the invitation level as a
  // sibling overlay (above opener z, above invitation z). When enabled it
  // owns the single guest gesture: cover-tap → starts shared audio, fires
  // openerStartRef so the opener timeline begins without a second tap,
  // and begins a 500ms fade-out before unmounting.
  //
  // Replay never re-shows the cover — `coverVisible` is only initialized
  // true on first mount when `event.entry_cover.enabled === true`. Once
  // dismissed it stays dismissed for the rest of the guest session.
  const [coverVisible, setCoverVisible] = useState(null);  // null = not yet decided (set when event loads)
  const [coverLeaving, setCoverLeaving] = useState(false);
  const openerStartRef = useRef(null);

  const handleReplayOpener = useCallback(() => {
    // Guard: only meaningful when an opener is enabled.
    if (event?.opener?.enabled !== true || !event?.opener?.presetId) return;
    // Don't start a replay while the opener is currently on screen.
    const stillPlaying = !openerCompleted && shouldPlayOpener({
      eventId: event.id,
      opener: event.opener,
      isPreviewOrDraft: !!previewToken || !!isPreviewResponse,
    });
    if (stillPlaying || isReplaying) return;
    replayScrollY.current = window.scrollY || window.pageYOffset || 0;
    setOpenerCompleted(false);
    // Phase 1.9.8 — reset reveal state so the crossfade replays cleanly.
    setRevealTriggered(false);
    setReplayKey((k) => k + 1);
  }, [event, openerCompleted, isReplaying, previewToken, isPreviewResponse]);

  const handleOpenerComplete = useCallback(() => {
    const wasReplay = replayKey > 0;
    const restoreY = replayScrollY.current;
    setOpenerCompleted(true);
    // Keep revealTriggered=true so the invitation stays at full
    // opacity (no jump back to 'active') as the overlay unmounts.
    setRevealTriggered(true);
    // If this completion was a manual Replay, restore the guest's
    // previous scroll position. We have to defer past two boundaries:
    // (1) React commit that unmounts the overlay, and (2) the browser
    // paint that releases the overflow:hidden lock. Two RAFs + a tiny
    // timeout has proven reliable across iOS Safari + Chrome Android.
    if (wasReplay) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, restoreY);
          // Belt-and-suspenders: in case a sibling effect runs a
          // synchronous reset during the same paint, restore again
          // after the next macrotask. Idempotent — no scroll if the
          // page is already where the user left it.
          setTimeout(() => { window.scrollTo(0, restoreY); }, 60);
        });
      });
    }
  }, [replayKey]);

  // Phase 1.9.8 — reset reveal state on event change is unnecessary;
  // the only writers of `revealTriggered` are the overlay callbacks
  // and replay handler. Phase is derived synchronously at render time.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Envelope state retained as a no-op for one release in case any
  // legacy code reads it. Phase 1.9.5 — the envelope is gone; this
  // value never transitions away from 'done' so the invitation is
  // always visible. See getInvitationStyle below.
  const envelopeState = 'done';
  const setEnvelopeState = () => {};
  // ─── Showcase Auto-scroll Mode ─────────────────────────────
  // 'waiting' → 'active' → 'paused'/'active' → 'finished'
  const [showcasePhase, setShowcasePhase] = useState(isShowcase ? 'waiting' : 'off');
  const scrollActiveRef = useRef(false);
  const rafIdRef = useRef(null);
  const visitedSectionsRef = useRef(new Set());
  const lastFrameRef = useRef(0);
  const phaseRef = useRef(showcasePhase);

  // Multi-language state
  const [activeLanguage, setActiveLanguage] = useState(null);  // set after data loads

  // Apply stored intrinsic dimensions to every <img> in the invitation so the
  // browser reserves the correct layout slot before each image loads (kills
  // Cumulative Layout Shift). Map is set via the Mobile Health Check action.
  useApplyImageDimensions(rsvpPage?.image_dimensions);

  // Keep ref in sync with state (avoids stale closures in rAF)
  useEffect(() => { phaseRef.current = showcasePhase; }, [showcasePhase]);

  // ─── Stale SW / cache rescue ─────────────────────────────────
  // One-shot, best-effort: if our bundle hash is older than the
  // deployed one, unregister any non-push SWs and wipe their caches.
  // This unblocks users stuck on a stale PWA cache after a deploy.
  useEffect(() => { purgeStaleServiceWorkers(); }, []);

  // Skip envelope & override scroll-behavior in showcase mode
  useEffect(() => {
    if (!isShowcase) return;
    setEnvelopeState('done');
    const html = document.documentElement;
    html.style.scrollBehavior = 'auto';
    html.classList.add('showcase-mode');
    return () => {
      html.style.scrollBehavior = '';
      html.classList.remove('showcase-mode');
    };
  }, [isShowcase]);

  // The core rAF scroll tick
  const scrollTick = useCallback((now) => {
    if (!scrollActiveRef.current) return;

    const dt = Math.min(now - lastFrameRef.current, 100);
    lastFrameRef.current = now;

    const px = (55 * dt) / 1000; // ~55 px/s cinematic pace
    window.scrollBy(0, px);

    // Reached bottom?
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    if (window.scrollY >= maxScroll - 2) {
      scrollActiveRef.current = false;
      setShowcasePhase('finished');
      return;
    }

    // Detect section boundaries
    const sections = document.querySelectorAll('[data-section-type]');
    for (const el of sections) {
      const sId = el.id || el.getAttribute('data-section-type');
      if (visitedSectionsRef.current.has(sId)) continue;
      const rect = el.getBoundingClientRect();
      if (rect.top > -30 && rect.top < 80) {
        visitedSectionsRef.current.add(sId);
        scrollActiveRef.current = false;
        setTimeout(() => {
          if (phaseRef.current === 'active') {
            scrollActiveRef.current = true;
            lastFrameRef.current = performance.now();
            rafIdRef.current = requestAnimationFrame(scrollTick);
          }
        }, 1500);
        return;
      }
    }

    rafIdRef.current = requestAnimationFrame(scrollTick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startShowcase = useCallback(() => {
    window.scrollTo(0, 0);
    visitedSectionsRef.current = new Set();
    // Mark all initially-visible sections so we don't pause on them
    const sections = document.querySelectorAll('[data-section-type]');
    for (const el of sections) {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        visitedSectionsRef.current.add(el.id || el.getAttribute('data-section-type'));
      }
    }
    scrollActiveRef.current = true;
    setShowcasePhase('active');
    lastFrameRef.current = performance.now();
    rafIdRef.current = requestAnimationFrame(scrollTick);
  }, [scrollTick]);

  const handleShowcaseTap = useCallback(() => {
    const p = phaseRef.current;
    if (p === 'waiting') {
      startShowcase();
    } else if (p === 'active') {
      scrollActiveRef.current = false;
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      setShowcasePhase('paused');
    } else if (p === 'paused') {
      scrollActiveRef.current = true;
      setShowcasePhase('active');
      lastFrameRef.current = performance.now();
      rafIdRef.current = requestAnimationFrame(scrollTick);
    } else if (p === 'finished') {
      // Restart from top
      startShowcase();
    }
  }, [startShowcase, scrollTick]);

  // Cleanup rAF on unmount
  useEffect(() => {
    return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
  }, []);
  // ─── End Showcase ──────────────────────────────────────────

  useEffect(() => {
    fetchInvitation();
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInvitation = async () => {
    try {
      setLoading(true);
      // Cache-bust on preview fetches. Also use native fetch with
      // `cache: 'reload'` — this is the strongest signal we can send
      // to the browser: ignore HTTP cache, ignore Service Worker
      // response caches, go to network. iOS Mail/WhatsApp webviews
      // honor this even when axios alone wasn't enough.
      const baseUrl = `${API}/api/public/invite/${slug}`;
      const url = previewToken
        ? `${baseUrl}?preview=${previewToken}&_ts=${Date.now()}`
        : baseUrl;
      try { window.__sitoaLastFetchUrl = url; } catch { /* noop */ }
      let payload;
      if (previewToken) {
        const r = await fetch(url, {
          cache: 'reload',
          credentials: 'omit',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        payload = await r.json();
      } else {
        const response = await axios.get(url);
        payload = response.data;
      }
      setRsvpPage(payload.rsvp_page);
      setEvent(payload.event);
      setIsPreviewResponse(!!payload.is_preview);
      // Phase 1.9.19 — initialize Entry Cover visibility from the event
      // record. coverVisible stays null until the event resolves so we
      // don't accidentally flash a cover during loading.
      setCoverVisible(payload.event?.entry_cover?.enabled === true);
      try { window.__sitoaResponseIdentity = payload?.response_identity || null; } catch { /* noop */ }

      // Multi-language: detect best language for this guest
      const evLangs = payload.event?.languages || [];
      const evDefault = payload.event?.default_language || 'en';
      if (evLangs.length > 1) {
        setActiveLanguage(detectGuestLanguage(evLangs, evDefault, slug));
      } else {
        setActiveLanguage(evDefault);
      }

      if (!payload.rsvp_page?.entry_animation || payload.rsvp_page.entry_animation === 'none') {
        setEnvelopeState('done');
      }
    } catch (err) {
      setError('Invitation not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRSVP = async (formData) => {
    try {
      setIsSubmitting(true);
      await axios.post(`${API}/api/public/invite/${slug}/rsvp`, formData);
      setSubmitSuccess(true);
    } catch (err) {
      console.error('Error submitting RSVP:', err);
      alert('Failed to submit RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Phase 1.9.5 — envelope removed. Handlers kept as no-ops in case any
  // child still wires them; they will be deleted in a later cleanup pass.
  const handleEnvelopeOpening = useCallback(() => {}, []);
  const handleEnvelopeComplete = useCallback(() => {}, []);

  const makeAbsoluteUrl = useCallback((url) => {
    if (!url) return url;
    if (url.startsWith('/')) return `${API}${url}`;
    return url;
  }, []);

  // Build absolute URLs for custom envelope assets (must be before early returns)
  const envelopeCustomAssets = useMemo(() => {
    const raw = rsvpPage?.envelope_assets;
    if (!raw || Object.keys(raw).length === 0) return null;
    const abs = {};
    for (const [k, v] of Object.entries(raw)) {
      abs[k] = makeAbsoluteUrl(v);
    }
    return abs;
  }, [rsvpPage?.envelope_assets, makeAbsoluteUrl]);

  // V2 engine detection (must be before early returns for hooks rules)
  const isV2 = rsvpPage?.engine === 'v2' && !!V2_TEMPLATES[rsvpPage?.v2_template_id];

  // Build V2 resolved content with absolute URLs + language overlay applied
  const v2ResolvedContent = useMemo(() => {
    if (!isV2) return {};
    // Apply translation overlay first (no-op if active === default)
    const langs = event?.languages || [];
    const defaultLang = event?.default_language || 'en';
    const useLang = activeLanguage || defaultLang;
    const baseContent = (langs.length > 1)
      ? resolveInvitationContent(
          rsvpPage?.v2_content || {},
          rsvpPage?.v2_translations || {},
          useLang,
          defaultLang,
        )
      : (rsvpPage?.v2_content || {});

    const resolved = {};
    for (const [sid, sdata] of Object.entries(baseContent)) {
      const r = { ...sdata };
      for (const [k, v] of Object.entries(r)) {
        if (typeof v === 'string' && v.startsWith('/api/')) {
          r[k] = `${API}${v}`;
        }
        if (Array.isArray(v)) {
          r[k] = v.map(item => typeof item === 'string' && item.startsWith('/api/') ? `${API}${item}` : item);
        }
      }
      resolved[sid] = r;
    }
    return resolved;
  }, [isV2, rsvpPage?.v2_content, rsvpPage?.v2_translations, event?.languages, event?.default_language, activeLanguage]);

  // Phase 1.9.18 — flatten v2 canvas elements + resolve the invitation
  // music URL. Hooks must be declared before any early return to keep
  // the React Hooks order stable across renders (otherwise loading/error
  // branches would skip these and break subsequent hooks).
  const v2CanvasElements = useMemo(() => {
    if (!isV2 || !v2ResolvedContent) return [];
    const out = [];
    for (const section of Object.values(v2ResolvedContent)) {
      const els = Array.isArray(section?.elements) ? section.elements : [];
      out.push(...els);
    }
    return out;
  }, [isV2, v2ResolvedContent]);
  const resolvedMusicUrl = useMemo(
    () => resolveInvitationMusicUrl({ rsvpPage, canvasElements: v2CanvasElements }),
    [rsvpPage, v2CanvasElements]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-light text-gray-800 mb-2">Oops!</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const TemplateComponent = TEMPLATE_COMPONENTS[rsvpPage?.template] || ClassicFormal;

  // Translation overlay for v1 template fields
  const v1OverlayResolve = (field) =>
    resolveV1Field(rsvpPage, field, rsvpPage?.v2_translations, activeLanguage, event?.default_language || 'en');

  const templateData = {
    ...rsvpPage,
    event_date: rsvpPage?.event_date || event?.date,
    venue_name: rsvpPage?.venue_name || event?.location,
    // Apply translations to v1 user-authored text fields
    story_text: v1OverlayResolve('story_text') ?? rsvpPage?.story_text,
    timeline: v1OverlayResolve('timeline') ?? rsvpPage?.timeline,
    envelope_message: v1OverlayResolve('envelope_message') ?? rsvpPage?.envelope_message,
    thank_you_message: v1OverlayResolve('thank_you_message') ?? rsvpPage?.thank_you_message,
    decline_message: v1OverlayResolve('decline_message') ?? rsvpPage?.decline_message,
    rsvp_intro: v1OverlayResolve('rsvp_intro') ?? rsvpPage?.rsvp_intro,
    details_intro: v1OverlayResolve('details_intro') ?? rsvpPage?.details_intro,
    details_text: v1OverlayResolve('details_text') ?? rsvpPage?.details_text,
    hero_image_url: makeAbsoluteUrl(rsvpPage?.hero_image_url),
    hero_photos: rsvpPage?.hero_photos?.map(makeAbsoluteUrl).filter(Boolean) || [],
    gallery_images: rsvpPage?.gallery_images?.map(makeAbsoluteUrl).filter(Boolean) || [],
    background_music_url: makeAbsoluteUrl(rsvpPage?.background_music_url),
    vinyl_cover_image: makeAbsoluteUrl(rsvpPage?.vinyl_cover_image),
    hero_bg_image: makeAbsoluteUrl(rsvpPage?.hero_bg_image),
  };

  const showEnvelope = false; // Phase 1.9.5 — envelope removed.
  const hasEnvelope = false;

  // Phase 1.9.5 — invitation is always fully visible. The cinematic
  // Opener overlay (z-9999, separate component) handles the entry beat.
  const getInvitationStyle = () => ({ opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' });

  // Phase 1.9.18 — resolve the invitation-level music URL using the
  // backward-compatible resolver. We pass v2 canvas elements when they
  // exist so a vinyl_player widget on the canvas can drive the shared
  // audio. v1 events with rsvp_page.background_music_url keep working
  // unchanged — the resolver picks that first.
  // (declared earlier, before the loading/error early returns, to keep
  // hook order stable across renders.)

  return (
    <InvitationAudioProvider musicUrl={resolvedMusicUrl}>
    <div className="min-h-screen relative public-invite-page">
      {/* ─── Entry Cover (Phase 1.9.19) ───────────────────────────────
          Strictly additive invitation-level overlay that owns the single
          guest gesture when enabled. Sits ABOVE the opener (z 10010).
          Replays never re-show the cover — coverVisible is gated on the
          initial event load and on `!isReplaying`. */}
      {coverVisible && !isReplaying && event?.entry_cover && (
        <EntryCoverBridge
          config={event.entry_cover}
          hasMusic={!!resolvedMusicUrl}
          leaving={coverLeaving}
          openerStartRef={openerStartRef}
          onDismissComplete={() => { setCoverVisible(false); setCoverLeaving(false); }}
          onBeginLeaving={() => setCoverLeaving(true)}
        />
      )}
      {/* ─── Cinematic opener (Phase 1.9) ────────────────────────────
          Mounts in two scenarios:
          (a) First visit / preview — `shouldPlayOpener` gate passes.
          (b) Guest tapped "Replay opening" from the footer — replayKey>0
              bypasses the seen-state gate but only for this remount.
          Sits ABOVE everything else (z-9999) and unmounts completely on
          completion/skip. `key={replayKey}` forces a clean remount on
          every manual replay so OpenerOverlay's useEffect runs fresh.
          Phase 1.9.18 — when the invitation has music AND this is the
          first play (not a replay), the overlay is gesture-gated: it
          waits for the guest's tap before starting animation + audio
          together. Replays don't re-gate per spec — they just replay
          the visual; audio state is preserved.
          Phase 1.9.19 — when Entry Cover is enabled AND currently
          showing, the cover owns the gesture: the opener is mounted
          but its timeline waits for openerStartRef to be fired. The
          opener does NOT render its own cue or accept its own taps. */}
      {event?.opener && !openerCompleted && (
        isReplaying ||
        shouldPlayOpener({
          eventId: event.id,
          opener: event.opener,
          isPreviewOrDraft: !!previewToken || !!isPreviewResponse,
        })
      ) && (
        <OpenerOverlayBridge
          key={`opener-${replayKey}`}
          eventId={event.id}
          opener={event.opener}
          gestureGated={!isReplaying && !coverVisible}
          externalStartRef={coverVisible && !isReplaying ? openerStartRef : null}
          onExitStart={handleOpenerExitStart}
          onComplete={handleOpenerComplete}
        />
      )}
      {/* ─── Preview / Draft badge ─────────────────────────────
          Visible only when the URL carries ?preview=TOKEN AND the
          backend confirms is_preview=true. Tells the designer at a
          glance that the device is rendering the latest draft, not
          the older published snapshot. Guests never set ?preview= so
          they never see this. */}
      {isPreviewResponse && <PreviewDraftBadge />}
      {layoutDebugEnabled && <LayoutDebugOverlay />}
      {/* When the URL carries ?preview= (designer mode), show the
          NewVersionBanner so the designer is alerted if their phone
          / desktop is running an older bundle than the latest deploy.
          Public guests (no preview token) never see this. */}
      {previewToken && <NewVersionBanner dirty={false} />}
      {/* ─── Showcase overlay (captures all taps) ─── */}
      {isShowcase && showcasePhase !== 'off' && (
        <div
          className="fixed inset-0 z-[300]"
          onClick={handleShowcaseTap}
          style={{ cursor: showcasePhase === 'finished' ? 'pointer' : 'default' }}
          data-testid="showcase-overlay"
        >
          {/* "Tap to begin" prompt */}
          {showcasePhase === 'waiting' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/15">
              <span
                className="text-white text-xs tracking-[0.25em] uppercase font-light px-6 py-3 rounded-full"
                style={{
                  animation: 'showcasePulse 2.5s ease-in-out infinite',
                  backgroundColor: 'rgba(0,0,0,0.35)',
                  backdropFilter: 'blur(4px)',
                  textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                }}
                data-testid="showcase-tap-prompt"
              >
                Tap anywhere to begin
              </span>
            </div>
          )}

          {/* Subtle pause indicator */}
          {showcasePhase === 'paused' && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
              <span
                className="text-white/30 text-[10px] tracking-[0.2em] uppercase font-light"
                data-testid="showcase-paused-indicator"
              >
                paused
              </span>
            </div>
          )}

          {/* Finished — subtle replay hint */}
          {showcasePhase === 'finished' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className="text-white/50 text-xs tracking-[0.2em] uppercase font-light"
                style={{ animation: 'showcasePulse 2.5s ease-in-out infinite' }}
                data-testid="showcase-replay-prompt"
              >
                Tap to replay
              </span>
            </div>
          )}
        </div>
      )}

      {/* Showcase mode: inject global keyframes + hide scrollbar */}
      {isShowcase && (
        <style>{`
          .showcase-mode { overflow-y: auto !important; }
          .showcase-mode::-webkit-scrollbar { display: none; }
          .showcase-mode { -ms-overflow-style: none; scrollbar-width: none; }
          @keyframes showcasePulse {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 0.85; }
          }
        `}</style>
      )}

      {/* Preview mode banner — removed per request. */}

      {/* Language dropdown — only when 2+ languages enabled on event.
          The component portals itself to document.body with viewport-fixed
          positioning, so it stays anchored to the top-right of the visible
          viewport across all scroll/background modes. */}
      {(event?.languages || []).length > 1 && activeLanguage && (
        <PublicLanguageSwitcher
          value={activeLanguage}
          onValueChange={(lang) => {
            setActiveLanguage(lang);
            saveGuestLanguagePreference(slug, lang);
          }}
          options={(event?.languages || []).map((code) => ({ code }))}
        />
      )}

      {/* Phase 1.9.5 — Envelope animation removed in favor of the new
          cinematic Opener overlay. */}

      {/* Invitation Template — crossfades in underneath the opener
          (Phase 1.9.8). When the opener is actively mounted we keep the
          invitation hidden+blurred; the moment the overlay fires
          onExitStart we flip to 'opener-revealing' which transitions
          opacity/scale/blur back to neutral over ~1.1s. */}
      <div
        className={(() => {
          const baseLayout = isV2 ? "min-h-screen" : "max-w-md mx-auto min-h-screen shadow-2xl";
          // Compute phase synchronously to avoid first-frame flicker.
          const openerStillUp = !!event?.opener && !openerCompleted && (
            isReplaying ||
            shouldPlayOpener({
              eventId: event.id,
              opener: event.opener,
              isPreviewOrDraft: !!previewToken || !!isPreviewResponse,
            })
          );
          let phaseClass = '';
          if (openerStillUp && !revealTriggered) phaseClass = 'opener-active';
          else if (revealTriggered) phaseClass = 'opener-revealing';
          // No opener configured → phaseClass='' → invitation fully visible immediately.
          return `${baseLayout} invitation-container ${phaseClass}`.trim();
        })()}
        data-testid="invitation-container"
      >
        {isV2 ? (
          <TemplateRenderer
            templateConfig={V2_TEMPLATES[rsvpPage.v2_template_id]}
            invitationData={v2ResolvedContent}
            themeOverrides={rsvpPage.v2_theme_overrides || {}}
            settingsOverrides={{
              ...resolveSettingsOverrides(
                rsvpPage.v2_settings_overrides || {},
                rsvpPage.v2_translations || {},
                activeLanguage,
                event?.default_language || 'en',
              ),
              eventDate: rsvpPage?.event_date || event?.date || '',
              venueName: rsvpPage?.venue_name || event?.location || '',
            }}
            slug={slug}
            layoutVersion={rsvpPage?.layout_version || 'legacy-flat'}
          />
        ) : (
          <TemplateComponent
            data={templateData}
            onSubmitRSVP={handleSubmitRSVP}
            isSubmitting={isSubmitting}
            submitSuccess={submitSuccess}
          />
        )}
      </div>

      <PerfAuditOverlay
        enabled={perfAuditEnabled}
        layoutVersion={rsvpPage?.layout_version || 'legacy-flat'}
        engine={rsvpPage?.engine}
      />

      {/* Phase 1.9.3 — discreet guest-initiated Replay action. Visible
          whenever the opener is enabled but NOT currently on screen — i.e.
          a returning visitor whose seen-once flag is already set, OR a
          first-visit guest who just finished the opener. Hidden during
          playback so it can't be tapped while the overlay is up. */}
      {event?.opener?.enabled === true && event?.opener?.presetId && !isReplaying && (
        openerCompleted ||
        !shouldPlayOpener({
          eventId: event.id,
          opener: event.opener,
          isPreviewOrDraft: !!previewToken || !!isPreviewResponse,
        })
      ) && (
        <div className="w-full flex justify-center py-6" data-testid="opener-replay-footer">
          <button
            type="button"
            onClick={handleReplayOpener}
            data-testid="opener-replay-link"
            className="text-[11px] uppercase tracking-[0.18em] text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 underline-offset-4 hover:underline transition-colors"
          >
            ▶ Replay opening
          </button>
        </div>
      )}
    </div>
    </InvitationAudioProvider>
  );
}

// Phase 1.9.18 — small bridge component so OpenerOverlay can consume the
// invitation-level audio (which is only accessible *inside* the provider
// tree). Lives in this file because it's tightly coupled to PublicInvite's
// composition; not worth a separate file.
function OpenerOverlayBridge({ gestureGated, externalStartRef, ...rest }) {
  const audio = useInvitationAudio();
  return (
    <OpenerOverlay
      {...rest}
      // Phase 1.9.19 — when Entry Cover owns the gesture, externalStartRef
      // is non-null. In that case we suppress the opener's own tap-gate
      // (gestureGated=false) and pass the ref through so the cover-tap
      // can fire the timeline imperatively.
      gestureGated={gestureGated && !!audio?.hasSource && !externalStartRef}
      externalStartRef={externalStartRef}
      onTap={async () => {
        // Returns whether playback actually started. Logged only; the
        // animation runs regardless so failure gracefully degrades.
        const ok = await (audio?.play?.() || Promise.resolve(false));
        return ok;
      }}
    />
  );
}

// Phase 1.9.19 — Entry Cover bridge. Owns the single guest gesture when
// the cover is enabled. On tap:
//   1) calls audio.play() SYNCHRONOUSLY inside the gesture handler so the
//      browser accepts it as user-initiated (autoplay policy)
//   2) fires openerStartRef so the opener animation begins WITHOUT
//      requiring a second tap
//   3) begins the cover fade-out — onBeginLeaving flips a parent state
//      that switches className → 500ms opacity transition → unmount
function EntryCoverBridge({ config, hasMusic, leaving, openerStartRef, onBeginLeaving, onDismissComplete }) {
  const audio = useInvitationAudio();
  const handleTap = async () => {
    // 1. Audio — synchronous start inside the gesture (no awaits before).
    //    play() returns a promise but we don't await it; rejection is OK.
    try { audio?.play?.(); } catch { /* ignore */ }
    // 2. Opener — fire its imperative start (no-op if no opener).
    try { openerStartRef?.current?.(); } catch { /* ignore */ }
    // 3. Begin fade.
    onBeginLeaving?.();
    // 4. After the fade, unmount.
    setTimeout(() => { onDismissComplete?.(); }, 550);
  };
  return (
    <EntryCover
      config={config}
      hasMusic={hasMusic}
      leaving={leaving}
      onTap={handleTap}
    />
  );
}
